import React from 'react';
import {Link} from 'react-router-dom';
import {useErrorNotyf, useShowMenu} from "hooks";
import {
  EledoPricingPlan, EledoError, EledoLoader, EledoRadioButton,
  EledoFormGenerator,
} from "components/library";
import {nanoid} from "nanoid";
import useCurrentPlan from 'graphql/hooks/useCurrentPlan';
import { EledoPlan, KeyValuePair } from 'graphql/EledoPlanState';
import { useMutation } from '@apollo/client';
import { CHANGE_PRICING_PLAN, EledoPlanChangeData, EledoPlanChangeVars } from 'graphql/EledoPlanChange';
import { BillingFormModel } from 'model';
import { EledoAccounting } from 'graphql/EledoAccountingQueries';
import { eledoAccountingInput } from 'graphql/hooks/useEledoAccounting';

const TabSubscription = ({accounting}: {accounting?: EledoAccounting}) => {
  const { loading, error, currentPlan } = useCurrentPlan()
  
  useErrorNotyf(error);
  useShowMenu();

  const renderError = () =>
    <EledoError title={error?.message}
                description={(<React.Fragment>
                  We are sorry &#9785; We could not load your pricing plans. <br/><br/>
                  {error?.message.toLocaleLowerCase() === "network error" && "Please check your internet connection."}
                </React.Fragment>)}/>;
  
  return (
    <div data-testid="TabPricing">
      { error && renderError() }
      <div >
        <div>
          {!error && !loading && currentPlan !== undefined &&
            <PricingPlanWithForm title="Your current plan"
                                plan={currentPlan.currentPlan}
                                subscription={{subscriptionType: currentPlan.subscriptionType, subscriptionTypes: currentPlan.subscriptionTypes}}
                                payment={{paymentMethod: currentPlan.paymentMethod, paymentMethods: currentPlan.paymentMethods}}/>
          }
          {
            !error && !loading && currentPlan !== undefined && currentPlan.nextPlan !== null &&
            <PricingPlanWithForm title={`Your next plan starting ${currentPlan.nextPlanStart ? new Date(currentPlan.nextPlanStart).toLocaleDateString() : "-"}`}
                                plan={currentPlan.nextPlan}
                                subscription={{subscriptionType: currentPlan.nextSubscriptionType, subscriptionTypes: currentPlan.subscriptionTypes}}
                                payment={{paymentMethod: currentPlan.nextPaymentMethod, paymentMethods: currentPlan.paymentMethods}}/>
          }
        </div>
        <div>
          <h3>Billing Address</h3>
          {accounting && <EledoFormGenerator
            formModel={BillingFormModel}
            editedDataObject={eledoAccountingInput(accounting)}
          />}
        </div>
      </div>
      { loading && <div className="flex-center"><EledoLoader/></div> }
    </div>
  );
}

export const PricingPlanWithForm = ({title, plan, subscription, payment}: {title: String, plan: EledoPlan,
   subscription: { subscriptionType: String, subscriptionTypes: KeyValuePair[]},
   payment: { paymentMethod: String, paymentMethods: KeyValuePair[]}}) => {

  const [ changePricingPlan ] = useMutation<EledoPlanChangeData, EledoPlanChangeVars>(CHANGE_PRICING_PLAN)

  const changePaymentDetails = () => {
    changePricingPlan({ variables: { data: {step: 10, successUrl: window.location.href}}})
      .then(result => {
        const planChange = result.data?.planChange
        if(planChange !== undefined && planChange.step === 11){
          window.location.href = planChange.settings.redirectUrl;
        } else {
          console.log("Failed to open payment details session", planChange)
        }
      })
      .catch(e => {
        console.log("Failed to open payment details session", e)
      })
  }

  return (
    <React.Fragment>
      <div>
        <h3>{title}</h3>

        <div className="col-wrapper">
          <div className="col-50">
            <EledoPricingPlan plan={plan} planButton={undefined}/>
          </div>

          <div className="col-50">
            <div style={{marginLeft: "1rem"}}>
              { plan.price > 0 && payment.paymentMethod === "CreditCard" &&
                <div style={{marginTop: "2rem"}}>
                  <a href="#/" className="w-button button" onClick={() => changePaymentDetails()}>Update Payment Details</a>
                </div>
              }
              <div>
                <Link className="w-button button" to={'/pricing'}>Modify Plan</Link>
              </div>
              { plan.price > 0 && payment.paymentMethod === "PayPal" &&
                <div style={{marginTop: "2rem"}}>
                <p>Payment Details cannot be changed for PayPal payment method. If you don't have a PayPal account and your subscription fails, you will need to cancel it and subscribe to Eledo again.</p>
              </div>
            }
              <PricingPlanDetails subscription={subscription} payment={payment}/>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export const PricingPlanDetails = ({subscription, payment}: {subscription: { subscriptionType: String, subscriptionTypes: KeyValuePair[]},
   payment: { paymentMethod: String, paymentMethods: KeyValuePair[]}}) => {
  const uniqueId = nanoid();
  return (
    <React.Fragment>
      <h3>Subscription Period</h3>

      { subscription.subscriptionTypes.map(st => (
          <EledoRadioButton id={`subscription-${uniqueId}-${st.key}`}
                            key={`subscription-${uniqueId}-${st.key}`}
                            label={st.value}
                            value={st.key === subscription.subscriptionType}
                            disabled/>
        )
      )}

      <h3>Payment Method</h3>

      { payment.paymentMethods.map(pm => (
          <EledoRadioButton id={`payment-${uniqueId}-${pm.key}`}
                            key={`payment-${uniqueId}-${pm.key}`}
                            label={pm.value}
                            value={pm.key === payment.paymentMethod}
                            disabled/>
        )
      )}

    </React.Fragment>
  );
};

export default TabSubscription;
